/**
 * vCard 3.0 export built on the client from contact rows and profiles. One card per person; the engine is never asked
 * for anything it does not already return to the people list.
 */
import type { ContactProfile, ContactRow } from "@hub/shared";

export interface VCardSource {
  row: ContactRow;
  profile: ContactProfile | null;
  notesCount: number;
}

/** Escapes a vCard text value (RFC 2426 section 2.4.2: backslash, comma, semicolon, newline). */
export function escapeVCardText(value: string): string {
  return value.replace(/\\/g, "\\\\").replace(/\n/g, "\\n").replace(/,/g, "\\,").replace(/;/g, "\;");
}

/** Folds a content line at 75 octets with a leading space on continuation lines. */
export function foldLine(line: string): string {
  const out: string[] = [];
  let rest = line;
  while (rest.length > 75) {
    out.push(rest.slice(0, 75));
    rest = " " + rest.slice(75);
  }
  out.push(rest);
  return out.join("\r\n");
}

function bday(profile: ContactProfile | null): string | null {
  const b = profile?.birthday;
  if (!b) return null;
  const mm = String(b.month).padStart(2, "0");
  const dd = String(b.day).padStart(2, "0");
  return b.year ? `${b.year}-${mm}-${dd}` : `--${mm}-${dd}`;
}

export function buildVCard(src: VCardSource): string {
  const { row, profile, notesCount } = src;
  const first = profile?.firstName ?? row.firstName ?? "";
  const last = profile?.lastName ?? row.lastName ?? "";
  const display = profile?.displayName ?? row.displayName;
  const lines: string[] = ["BEGIN:VCARD", "VERSION:3.0"];
  lines.push(`FN:${escapeVCardText(display)}`);
  lines.push(`N:${escapeVCardText(last)};${escapeVCardText(first)};;;`);
  if (row.primaryEmail) lines.push(`EMAIL;TYPE=INTERNET:${escapeVCardText(row.primaryEmail)}`);
  if (row.primaryPhone) lines.push(`TEL:${escapeVCardText(row.primaryPhone)}`);
  const company = profile?.company ?? row.company;
  if (company) lines.push(`ORG:${escapeVCardText(company)}`);
  const title = profile?.title ?? null;
  if (title) lines.push(`TITLE:${escapeVCardText(title)}`);
  for (const url of profile?.websites ?? []) lines.push(`URL:${escapeVCardText(url)}`);
  const b = bday(profile);
  if (b) lines.push(`BDAY:${b}`);
  const noteParts: string[] = [];
  if (profile?.headline) noteParts.push(profile.headline);
  const location = profile?.location ?? row.location;
  if (location) noteParts.push(`Location: ${location}`);
  noteParts.push(`${notesCount} ${notesCount === 1 ? "note" : "notes"} in Hub`);
  lines.push(`NOTE:${escapeVCardText(noteParts.join("\n"))}`);
  lines.push("END:VCARD");
  return lines.map(foldLine).join("\r\n");
}

export function buildVCards(sources: VCardSource[]): string {
  return sources.map(buildVCard).join("\r\n") + "\r\n";
}
